import { loadingButtonClasses } from '@mui/lab'
import type { Components, CSSObject, Theme } from '@mui/material'
import { buttonClasses, iconButtonClasses, svgIconClasses } from '@mui/material'

declare module '@mui/material/Button' {
  export interface ButtonPropsVariantOverrides {
    ghost: true
  }

  export interface ButtonPropsSizeOverrides {
    tiny: true
  }

  export interface ButtonPropsColorOverrides {
    golden: true
  }
}

export const MuiButton = (theme: Theme): Components['MuiButton'] => ({
  defaultProps: {
    disableElevation: true,
  },
  styleOverrides: {
    root: {
      whiteSpace: 'nowrap',
      borderRadius: +theme.shape.borderRadius * 2,
      padding: '8px 16px',
      minWidth: 'initial',
      ...theme.typography.BodyMedium,
      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: theme.palette.bg3,
        color: theme.palette.text3,
      },
      [`.${loadingButtonClasses.loadingIndicator}`]: {
        color: theme.palette.text1,
      },
      '&:before': {
        content: '""',
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        borderRadius: 'inherit',
        transition: `background-color .2s ease`,
        pointerEvents: 'none',
      },
      '&:hover': {
        '&:before': {
          backgroundColor: theme.palette.hover,
        },
      },
    },
    sizeLarge: {
      ...(theme.typography.BodyLargeMedium as CSSObject),
      padding: '10px 16px',
    },
    sizeMedium: {
      ...(theme.typography.BodyMedium as CSSObject),
      padding: '8px 16px',
    },
    sizeSmall: {
      ...(theme.typography.CaptionMedium as CSSObject),
      padding: '6px 16px',
      [`.${buttonClasses.startIcon}`]: {
        '&>*:nth-of-type(1)': {
          fontSize: theme.typography.pxToRem(20),
        },
      },
    },
    startIcon: {
      margin: 0,
      '&>*:nth-of-type(1)': {
        fontSize: theme.typography.pxToRem(24),
      },
    },
    endIcon: {
      margin: 0,
      '&>*:nth-of-type(1)': {
        fontSize: theme.typography.pxToRem(24),
      },
    },
  },
  variants: [
    {
      props: { variant: 'contained' },
      style: {
        [`&.${buttonClasses.disabled}`]: {
          opacity: 0.4,
          backgroundColor: theme.palette.bg4,
          color: theme.palette.text1,
        },
      },
    },
    {
      props: { variant: 'contained', color: 'primary' },
      style: {
        color: theme.palette.onNegativeText,
        '&:hover': {
          backgroundColor: theme.palette.primaryBrandBg6,
        },
      },
    },
    {
      props: { variant: 'contained', color: 'secondary' },
      style: {
        color: theme.palette.onColorText,
        '&:hover': {
          opacity: 0.9,
        },
      },
    },
    {
      props: { variant: 'contained', color: 'error' },
      style: {
        color: theme.palette.onColorText,
      },
    },
    {
      props: { variant: 'contained', color: 'success' },
      style: {
        color: theme.palette.onColorText,
      },
    },
    {
      props: { variant: 'contained', color: 'warning' },
      style: {
        color: theme.palette.onColorText,
      },
    },
    {
      props: { variant: 'contained', color: 'golden' },
      style: {
        color: theme.palette.onColorDarkText,
        backgroundImage: theme.palette.gradGolden(270),
      },
    },
    {
      props: { variant: 'ghost' },
      style: {
        [`&.${buttonClasses.disabled}`]: {
          opacity: 0.4,
          backgroundColor: theme.palette.bg4,
          color: theme.palette.text1,
        },
      },
    },
    {
      props: { variant: 'ghost', color: 'primary' },
      style: {
        backgroundColor: theme.palette.primaryBrandBgGlass2,
        color: theme.palette.primaryBrandText,
        '&:hover': {
          backgroundColor: theme.palette.primaryBrandBgGlass2,
        },
      },
    },
    {
      props: { variant: 'ghost', color: 'secondary' },
      style: {
        backgroundColor: theme.palette.glassBg2,
        color: theme.palette.text2,
        '&:hover': {
          backgroundColor: theme.palette.glassBg2,
        },
      },
    },
    {
      props: { variant: 'ghost', color: 'error' },
      style: {
        backgroundColor: theme.palette.criticalBg3,
        color: theme.palette.criticalText,
        '&:hover': {
          backgroundColor: theme.palette.criticalBg3,
        },
      },
    },
    {
      props: { variant: 'ghost', color: 'success' },
      style: {
        backgroundColor: theme.palette.approveBg3,
        color: theme.palette.approveText,
        '&:hover': {
          backgroundColor: theme.palette.approveBg3,
        },
      },
    },
    {
      props: { variant: 'ghost', color: 'golden' },
      style: {
        backgroundColor: theme.palette.warningBg2,
        color: theme.palette.warningText,
      },
    },
    {
      props: { variant: 'outlined' },
      style: {
        [`&.${buttonClasses.disabled}`]: {
          opacity: 0.4,
          borderColor: theme.palette.stroke4,
          color: theme.palette.text1,
        },
      },
    },
    {
      props: { variant: 'outlined', color: 'primary' },
      style: {
        borderColor: theme.palette.primaryBrandStroke2,
        color: theme.palette.primaryBrandText,
        '&:hover': {
          borderColor: theme.palette.primaryBrandStroke2,
          backgroundColor: 'transparent',
        },
      },
    },
    {
      props: { variant: 'outlined', color: 'secondary' },
      style: {
        borderColor: theme.palette.stroke4,
        color: theme.palette.text1,
        '&:hover': {
          borderColor: theme.palette.stroke4,
          backgroundColor: 'transparent',
        },
      },
    },
    {
      props: { variant: 'outlined', color: 'error' },
      style: {
        borderColor: theme.palette.criticalStroke2,
        color: theme.palette.criticalText,
        '&:hover': {
          borderColor: theme.palette.criticalStroke2,
          backgroundColor: 'transparent',
        },
      },
    },
    {
      props: { variant: 'outlined', color: 'success' },
      style: {
        borderColor: theme.palette.approveStroke2,
        color: theme.palette.approveText,
        '&:hover': {
          borderColor: theme.palette.approveStroke2,
          backgroundColor: 'transparent',
        },
      },
    },
    {
      props: { variant: 'outlined', color: 'warning' },
      style: {
        borderColor: theme.palette.stroke2,
      },
    },
    {
      props: { variant: 'outlined', color: 'golden' },
      style: {
        borderColor: '#EFBD3B',
        color: theme.palette.warningText,
      },
    },
    {
      props: { variant: 'text' },
      style: {
        paddingLeft: 8,
        paddingRight: 8,
        [`.${buttonClasses.startIcon}`]: {
          marginLeft: -4,
        },
        [`.${buttonClasses.endIcon}`]: {
          marginRight: -4,
        },
        [`&.${buttonClasses.disabled}`]: {
          opacity: 0.4,
          color: theme.palette.text1,
        },
      },
    },
    {
      props: { variant: 'text', color: 'primary' },
      style: {
        color: theme.palette.primary.main,
      },
    },
    {
      props: { variant: 'text', color: 'secondary' },
      style: {
        color: theme.palette.primaryBrandText,
      },
    },
    {
      props: { variant: 'text', color: 'error' },
      style: {
        color: theme.palette.criticalText,
      },
    },
    {
      props: { variant: 'text', color: 'success' },
      style: {
        color: theme.palette.approveText,
      },
    },
    {
      props: { variant: 'text', color: 'golden' },
      style: {
        color: theme.palette.warningText,
      },
    },
    {
      props: {
        size: 'tiny',
      },
      style: {
        padding: theme.spacing(0.25, 0.75),
        borderRadius: theme.shape.borderRadius * 1.5,
        ...(theme.typography.CaptionRegular as CSSObject),
        [`.${buttonClasses.endIcon}`]: {
          marginRight: -0.25,
          '&>*:nth-of-type(1)': {
            fontSize: theme.typography.pxToRem(20),
          },
        },
      },
    },
  ],
})

declare module '@mui/material/IconButton' {
  export interface IconButtonPropsSizeOverrides {
    tiny: true
  }
}
export const MuiIconButton = (theme: Theme): Components['MuiIconButton'] => ({
  defaultProps: {
    TouchRippleProps: {
      center: false,
    },
  },
  styleOverrides: {
    root: {
      borderRadius: +theme.shape.borderRadius * 2,
      color: theme.palette.text2,
      [`.${svgIconClasses.root}, svg`]: {
        fontSize: 'inherit',
      },
      [`&.${iconButtonClasses.disabled}`]: {
        backgroundColor: theme.palette.bg3,
        color: theme.palette.text3,
      },
      '&:hover': {
        backgroundColor: theme.palette.hover,
      },
    },
    sizeLarge: {
      fontSize: theme.typography.pxToRem(24),
    },
    sizeMedium: {
      padding: '6px',
    },
    sizeSmall: {
      fontSize: theme.typography.pxToRem(16),
      padding: '6px',
    },
  },
  variants: [
    {
      props: {
        size: 'tiny',
      },
      style: {
        fontSize: theme.typography.pxToRem(12),
        padding: theme.spacing(0.4),
      },
    },
  ],
})
